/** Dispersión de dos métricas mensuales. Una sola serie: sin leyenda, el título la nombra.
 *  Cada punto lleva su mes en el tooltip, que es lo que convierte la nube en una historia. */
import { useState } from "react";
import type { Grafico, Idioma } from "../lib/tipos";
import { num } from "../lib/formato";
import { ANCHO_MOVIL, Ejes, MARGEN, Marco, Tooltip, escalaLineal, ticks, useEsMovil } from "./comun";
import { bi } from "../lib/i18n";

const ANCHO = 500;
const ALTO = 300;

export function GraficoDispersion({ grafico, idioma }: { grafico: Grafico; idioma: Idioma }) {
  const puntos = (grafico.puntos ?? []).filter((p) => p.y !== null && typeof p.x === "number") as { x: number; y: number; etiqueta?: string }[];
  const [activo, setActivo] = useState<number | null>(null);
  const movil = useEsMovil();
  const anchoV = movil ? ANCHO_MOVIL : ANCHO;
  const altoV = movil ? 280 : ALTO;
  const cuerpo = movil ? 13 : 10;
  if (puntos.length < 2) return null;

  const xs = puntos.map((p) => p.x);
  const ys = puntos.map((p) => p.y);
  const hx = (Math.max(...xs) - Math.min(...xs)) * 0.1 || 1;
  const hy = (Math.max(...ys) - Math.min(...ys)) * 0.1 || 1;
  const tx = escalaLineal(Math.min(...xs) - hx, Math.max(...xs) + hx, MARGEN.izquierda, anchoV - MARGEN.derecha);
  const ty = escalaLineal(Math.min(...ys) - hy, Math.max(...ys) + hy, altoV - MARGEN.abajo, MARGEN.arriba);
  const ticksX = ticks(Math.min(...xs) - hx, Math.max(...xs) + hx, 4);

  return (
    <Marco titulo={grafico.titulo} idioma={idioma} ancho={anchoV} alto={altoV}
           describe={`${bi(grafico.eje_x, idioma)} vs ${bi(grafico.eje_y, idioma)}`}>
      <svg viewBox={`0 0 ${anchoV} ${altoV}`} width="100%" height="100%" onMouseLeave={() => setActivo(null)}>
        <Ejes ancho={anchoV} alto={altoV} tx={(v) => tx(Number(v))} ty={ty}
              ticksY={ticks(Math.min(...ys) - hy, Math.max(...ys) + hy, movil ? 3 : 4)} etiquetasX={ticksX}
              cuerpo={cuerpo} maxEtiquetas={movil ? 4 : 8} />
        {puntos.map((p, i) => (
          <circle key={i} cx={tx(p.x)} cy={ty(p.y)} r={activo === i ? 7 : 5}
                  fill="var(--serie-1)" fillOpacity={activo === null || activo === i ? 0.85 : 0.35}
                  stroke="var(--superficie-1)" strokeWidth="2"
                  onMouseEnter={() => setActivo(i)} />
        ))}
        <text x={(anchoV + MARGEN.izquierda) / 2} y={altoV - 4} textAnchor="middle" fontSize={cuerpo} fill="var(--texto-3)">
          {bi(grafico.eje_x, idioma)}
        </text>
        <text x={12} y={MARGEN.arriba + 4} fontSize={cuerpo} fill="var(--texto-3)">{bi(grafico.eje_y, idioma)}</text>
        {activo !== null && puntos[activo] && (
          <Tooltip x={tx(puntos[activo]!.x)} y={ty(puntos[activo]!.y)} ancho={anchoV}
                   lineas={[puntos[activo]!.etiqueta ?? "",
                            `${bi(grafico.eje_x, idioma)}: ${num(puntos[activo]!.x, 2)}`,
                            `${bi(grafico.eje_y, idioma)}: ${num(puntos[activo]!.y, 2)}`]} />
        )}
      </svg>
    </Marco>
  );
}
